import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { WHATSAPP_NUMBER } from '@constants/contact'
import LoginContext from '@contexts/Login'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import * as $Auth from '@services/Auth'
import AuthContext from '@contexts/Auth'
import axios from 'axios'
import '@resources/css/securitycode.css'
import { MdKeyboardArrowLeft } from 'react-icons/md'
import { MdKeyboardArrowRight } from 'react-icons/md'
import { FaShieldAlt } from 'react-icons/fa'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

const SecurityCode: React.FC<any> = () => {
  const { setUser, setToken, setIsAuthenticated } = useContext(AuthContext)
  const { email, password, isLoading, setIsLoading, error, setError, codeExpiresAt, setCodeExpiresAt } = useContext(LoginContext)
  const navigate = useNavigate()
  const SweetAlert = withReactContent(Swal)

  const [ donorId, setDonorId ] = useState<string | null>(null)
  const [ securityCode, setSecurityCode ] = useState<string[]>(['', '', '', ''])
  const [ isLoadingResend, setIsLoadingResend ] = useState(false)
  const [ resendTimer, setResendTimer ] = useState(30)
  const [ expiresIn, setExpiresIn ] = useState<number>(0)
  const timerRef = useRef<number | null>(null)
  const inputRefs = useRef<HTMLInputElement[]>([])

  const isExpired = !codeExpiresAt || expiresIn <= 0

  useEffect(() => {
    const tick = () => {
      if (!codeExpiresAt) {
        setExpiresIn(0)
        return
      }

      const diff = Math.floor((new Date(codeExpiresAt).getTime() - Date.now()) / 1000)
      setExpiresIn(diff > 0 ? diff : 0)
    }

    tick()
    const interval = window.setInterval(tick, 1000)

    return () => clearInterval(interval)
  }, [ codeExpiresAt ])

  const [ searchParams ] = useSearchParams()

  useEffect(() => {
    if (searchParams.has('doador')) {
      const donorId = searchParams.get('doador')

      if (donorId) {
        setDonorId(donorId)
      }
    } else {
      setDonorId(null)
    }
  }, [ searchParams ])

  useEffect(() => {
    const startTimer = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }

      timerRef.current = window.setInterval(() => {
        setResendTimer(current => {
          if (current <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current)
              timerRef.current = null
            }
            return 0
          }

          return current - 1
        })
      }, 1000)
    }

    if (resendTimer > 0 && !timerRef.current) {
      startTimer()
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [resendTimer])

  const startResendTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    setResendTimer(60)
    timerRef.current = window.setInterval(() => {
      setResendTimer(current => {
        if (current <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
          return 0
        }

        return current - 1
      })
    }, 1000)
  }, [])

  const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const values = [...securityCode]
    values[index] = event.target.value
    setSecurityCode(values)

    if (event.target.value.length === 1) {
      const inputRef = inputRefs.current[index + 1] || inputRefs.current[index]
      inputRef.focus()
    }
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()

      setSecurityCode(securityCode => {
        const values = [...securityCode]
        values[index] = ''
        return values
      })

      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus()
      }
    }
  }

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault()
    const paste = event.clipboardData.getData('text')

    let index = 0

    for (let i = 0; i < 4; i++) {
      inputRefs.current[i].value = paste[index]

      index++
      if (index === 4)
        break
    }

    setSecurityCode(inputRefs.current.map(input => input.value))
  }

  const resend = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (resendTimer > 0 || isLoadingResend) {
      return
    }

    setIsLoadingResend(true)
    setError(null)
    startResendTimer()

    SweetAlert.fire({
      title: 'Aguarde...',
      text: 'Reenviando código de segurança...',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => {
        SweetAlert.showLoading()
      },
    })

    $Auth.securityCode(email, password, true)
      .then(({ data }) => {
        setCodeExpiresAt(data?.expires_at ?? null)
        SweetAlert.fire({
          title: 'Sucesso!',
          text: 'O código de segurança foi reenviado. Verifique seu e-mail e WhatsApp.',
          icon: 'success',
          confirmButtonText: 'Ok',
        })
      })
      .catch(e => {
        const message = e.response?.data?.message || 'Ocorreu um erro ao tentar realizar o login. Favor tentar novamente.'
        setError(message)
        SweetAlert.fire({
          title: 'Erro!',
          text: message,
          icon: 'error',
          confirmButtonText: 'Ok',
        })
      })
      .finally(() => setIsLoadingResend(false))
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (isExpired) {
      setError('O código de segurança expirou. Clique para reenviar um novo.')
      return
    }

    setIsLoading(true)

    setError(null)

    $Auth.login(email, password, securityCode.join('')).then(({ data: { user, token } }) => {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      setUser(user)
      setToken(token)
      setIsAuthenticated(true)

      SweetAlert.fire({
        title: 'Bem-vindo(a) de volta!',
        text: 'Você já pode explorar nosso catálogo de doadores.',
        icon: 'success',
        confirmButtonText: 'Acessar catálogo',
      }).then(() => {
        navigate(donorId ? `/doadores/${donorId}` : '/home')
      })
    }).catch((e) => {
      setError(
        e.response?.data?.message || 'Ocorreu um erro ao tentar realizar o login. Favor tentar novamente.'
      )
    }).finally(() => setIsLoading(false))
  }

  return (
    <div className="row">

      <div className="col-12 col-md-8 offset-md-2">
        <div className="card securitycode card-full">
          <div className="card-header d-flex flex-column align-items-center pt-3">
            <figure>
              <FaShieldAlt size={120} style={{ color: '#FFA9BF' }} />
            </figure>
          </div>
          <form onSubmit={onSubmit}>
            <div className="card-body d-flex flex-column align-items-center">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <strong>Atenção!</strong> {error}
                </div>
              )}
              <h3>
                Você recebeu um código de segurança via e-mail e/ou WhatsApp!
              </h3>
              <p>Enviamos o código para <span>{email}</span></p>
              <div className="row">
                {securityCode.map((code, index) => (
                  <div className="col-3" key={index}>
                    <input
                      ref={input => {
                        if (input) inputRefs.current[index] = input
                      }}
                      type="number"
                      className="form-control"
                      value={code}
                      onChange={event => handleChange(index, event)}
                      onKeyDown={event => handleKeyDown(index, event)}
                      onPaste={handlePaste}
                      maxLength={1}
                    />
                  </div>
                ))}
              </div>
              <p className="code-expiration">
                {isExpired ? (
                  <span className="code-expiration--expired">Código expirado. Solicite um novo.</span>
                ) : (
                  <span>Expira em {String(Math.floor(expiresIn / 60)).padStart(2, '0')}:{String(expiresIn % 60).padStart(2, '0')}</span>
                )}
              </p>
              <div className="link-codigo">
                <p>
                  Não recebeu um código? Verifique seu spam ou{' '}
                  {resendTimer > 0 ? (
                    <span className="resend-disabled">
                      reenviar em {String(resendTimer).padStart(2, '0')}s
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="resend-btn"
                      onClick={resend}
                      disabled={isLoadingResend}
                    >
                      <span>clique para reenviar</span>
                    </button>
                  )}
                </p>
                <p>Se estiver com dificuldades, nos chame por <Link target="_blank" to={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Estou com dificuldades para acessar minha conta.')}`}><span>WhatsApp</span></Link></p>
              </div>
            </div>
            <div className="card-footer d-flex align-items-center justify-content-center pb-3">
              <div className="row">
                <div className="col-6 col-lg-6">
                  <Link className="return-btn btn btn-primary" to="/">
                    <MdKeyboardArrowLeft />
                    Cancelar
                  </Link>
                </div>
                <div className="col-6 col-lg-6">
                  <button type="submit" className="register-btn btn btn-primary" disabled={isLoading || isLoadingResend || isExpired}>
                    Verificar
                    <MdKeyboardArrowRight />
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SecurityCode
